/**
 * /settings/mcp — MCP (Model Context Protocol) server registry.
 *
 * Lets admins register external MCP servers (Linear, GitHub, Notion, custom
 * internal services) so the user's own agents and integrations are available
 * to ledger-core's AI features.
 *
 * Today this page does:
 *   - CRUD (list / add / edit / delete)
 *   - Connection test that probes initialize + tools/list
 *
 * Next phase will wire enabled servers' tools into the chat / agent layer.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, errorMessage } from "@/lib/api";
import { useToast } from "@/components/Toaster";

export const Route = createFileRoute("/_auth/settings/mcp")({
  component: MCPPage,
});

type MCPServer = {
  id: string;
  name: string;
  transport: "http";
  url: string | null;
  has_auth_token: boolean;
  headers: Record<string, string> | null;
  description: string | null;
  enabled: boolean;
  last_connected_at: string | null;
  last_error: string | null;
  tool_count: number;
  discovered_tools: { name: string; description: string | null }[] | null;
  created_at: string;
  updated_at: string;
};

type MCPTestResult = {
  ok: boolean;
  latency_ms: number;
  tool_count: number;
  tools: { name: string; description: string | null }[];
  error: string | null;
};

function MCPPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<MCPServer | "new" | null>(null);

  const serversQ = useQuery({
    queryKey: ["mcp-servers"],
    queryFn: () => api<MCPServer[]>("/api/admin/mcp-servers"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      api(`/api/admin/mcp-servers/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast.success("MCP server removed");
      void qc.invalidateQueries({ queryKey: ["mcp-servers"] });
    },
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api<MCPServer>(`/api/admin/mcp-servers/${id}`, {
        method: "PATCH",
        body: { enabled },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["mcp-servers"] }),
  });

  const servers = serversQ.data ?? [];

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden bg-slate-50 dark:bg-gray-950">
      <div className="bg-white dark:bg-gray-900 border-b border-slate-300 dark:border-gray-800 shadow-sm dark:shadow-none px-6 py-4 flex-shrink-0">
        <div className="flex items-center gap-3 mb-0.5">
          <Link to="/settings/integrations" className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 text-sm">
            ← Setup
          </Link>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              MCP Servers
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Bring your own agents and integrations via the Model Context Protocol.
            </p>
          </div>
          <button
            onClick={() => setEditing("new")}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition"
          >
            + Add server
          </button>
        </div>
      </div>

      <div className="overflow-y-auto flex-1 px-6 py-6">
        <div className="max-w-5xl mx-auto">
          {/* Intro card */}
          <div className="mb-6 p-4 rounded-xl border border-slate-300 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm dark:shadow-none">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">
              What's MCP?
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
              The <a className="text-blue-600 dark:text-blue-400 hover:underline" href="https://modelcontextprotocol.io" target="_blank" rel="noopener noreferrer">Model Context Protocol</a> is
              an open standard for connecting AI agents to external tools and data.
              Register hosted MCP servers here (Linear, GitHub, Notion, Slack, custom
              internal services) and ledger-core's AI features will be able to call
              the tools they expose. Servers stay disabled until you test the
              connection and explicitly enable them.
            </p>
          </div>

          {serversQ.isLoading && (
            <div className="text-center text-slate-500 dark:text-slate-400 py-12">Loading…</div>
          )}

          {serversQ.isSuccess && servers.length === 0 && (
            <div className="text-center py-16 bg-white dark:bg-gray-900 rounded-xl border border-dashed border-slate-300 dark:border-gray-800">
              <p className="text-4xl mb-3 opacity-50">🔌</p>
              <p className="text-base font-medium text-slate-700 dark:text-slate-300">No MCP servers yet</p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 mb-4">
                Add your first one to expand what ledger-core's AI can do.
              </p>
              <button
                onClick={() => setEditing("new")}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition"
              >
                + Add server
              </button>
            </div>
          )}

          {servers.length > 0 && (
            <div className="space-y-3">
              {servers.map((s) => (
                <ServerCard
                  key={s.id}
                  server={s}
                  onEdit={() => setEditing(s)}
                  onDelete={() => {
                    if (window.confirm(`Remove '${s.name}'?`)) deleteMut.mutate(s.id);
                  }}
                  onToggle={(enabled) => toggleMut.mutate({ id: s.id, enabled })}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {editing && (
        <ServerDialog
          server={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void qc.invalidateQueries({ queryKey: ["mcp-servers"] });
          }}
        />
      )}
    </div>
  );
}

function ServerCard({
  server,
  onEdit,
  onDelete,
  onToggle,
}: {
  server: MCPServer;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: (enabled: boolean) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const testMut = useMutation({
    mutationFn: () =>
      api<MCPTestResult>(`/api/admin/mcp-servers/${server.id}/test`, { method: "POST" }),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(`Connected · ${r.tool_count} tool${r.tool_count === 1 ? "" : "s"} (${r.latency_ms}ms)`);
      } else {
        toast.error(`Test failed: ${r.error ?? "unknown error"}`);
      }
      void qc.invalidateQueries({ queryKey: ["mcp-servers"] });
    },
  });

  return (
    <div className="bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-800 shadow-sm dark:shadow-none rounded-xl overflow-hidden">
      <div className="p-4 flex items-start gap-4">
        <div className={`w-2.5 h-2.5 rounded-full mt-2 flex-shrink-0 ${
          server.enabled
            ? server.last_error ? "bg-amber-500" : "bg-emerald-500"
            : "bg-slate-300 dark:bg-slate-600"
        }`} title={
          server.enabled
            ? server.last_error ? "Enabled but last test failed" : "Enabled"
            : "Disabled"
        } />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 truncate">{server.name}</h3>
            <span className="text-xs text-slate-500 dark:text-slate-500 font-mono uppercase tracking-wider">
              {server.transport}
            </span>
            {server.tool_count > 0 && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-slate-400">
                {server.tool_count} tool{server.tool_count === 1 ? "" : "s"}
              </span>
            )}
            {server.has_auth_token && (
              <span className="text-xs text-emerald-700 dark:text-emerald-400">🔒 auth</span>
            )}
          </div>
          {server.url && (
            <p className="text-xs text-slate-500 dark:text-slate-400 font-mono mt-0.5 truncate">{server.url}</p>
          )}
          {server.description && (
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1.5">{server.description}</p>
          )}
          {server.last_error && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-2">
              ⚠️ Last test: {server.last_error}
            </p>
          )}
          {server.last_connected_at && !server.last_error && (
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
              ✓ Last connected {new Date(server.last_connected_at).toLocaleString()}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs">
            <input
              type="checkbox"
              checked={server.enabled}
              onChange={(e) => onToggle(e.target.checked)}
              className="w-4 h-4 rounded text-blue-600"
            />
            <span className="text-slate-600 dark:text-slate-400">Enabled</span>
          </label>
          <button
            onClick={() => testMut.mutate()}
            disabled={testMut.isPending}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 dark:border-gray-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            {testMut.isPending ? "Testing…" : "Test"}
          </button>
          <button
            onClick={onEdit}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 dark:border-gray-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-gray-800"
          >
            Edit
          </button>
          <button
            onClick={onDelete}
            className="px-3 py-1.5 text-sm rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Discovered tools (collapsible — shown after a successful test) */}
      {server.discovered_tools && server.discovered_tools.length > 0 && (
        <details className="border-t border-slate-200 dark:border-gray-800">
          <summary className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400 cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-800/50">
            Show {server.discovered_tools.length} discovered tool{server.discovered_tools.length === 1 ? "" : "s"}
          </summary>
          <ul className="px-4 py-2 text-xs space-y-1 max-h-60 overflow-y-auto">
            {server.discovered_tools.map((t) => (
              <li key={t.name} className="flex gap-2 py-0.5">
                <span className="font-mono text-blue-700 dark:text-blue-400 flex-shrink-0">{t.name}</span>
                {t.description && (
                  <span className="text-slate-500 dark:text-slate-400 truncate">— {t.description}</span>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function ServerDialog({
  server,
  onClose,
  onSaved,
}: {
  server: MCPServer | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = server === null;
  const [name, setName] = useState(server?.name ?? "");
  const [url, setUrl] = useState(server?.url ?? "");
  const [authToken, setAuthToken] = useState("");
  const [clearToken, setClearToken] = useState(false);
  const [description, setDescription] = useState(server?.description ?? "");
  const [enabled, setEnabled] = useState(server?.enabled ?? false);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (isNew) {
        await api("/api/admin/mcp-servers", {
          method: "POST",
          body: {
            name: name.trim(),
            transport: "http",
            url: url.trim(),
            auth_token: authToken.trim() || null,
            description: description.trim() || null,
            enabled,
          },
        });
      } else {
        const body: Record<string, unknown> = {
          name: name.trim(),
          url: url.trim(),
          description: description.trim() || null,
          enabled,
        };
        // Token semantics: "" = clear, omitted = leave alone, anything = set
        if (clearToken) body.auth_token = "";
        else if (authToken.trim()) body.auth_token = authToken.trim();
        await api(`/api/admin/mcp-servers/${server!.id}`, {
          method: "PATCH",
          body,
        });
      }
    },
    onSuccess: onSaved,
  });

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 dark:bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-slate-300 dark:border-gray-700 max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 dark:border-gray-800">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {isNew ? "Add MCP server" : `Edit · ${server!.name}`}
          </h2>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Name" hint="Display name for this server">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Linear, GitHub, …"
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100"
            />
          </Field>
          <Field label="URL" hint="HTTPS endpoint of the MCP server's /mcp route">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://mcp.linear.app/mcp"
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100 font-mono"
            />
          </Field>
          <Field
            label="Auth token (Bearer)"
            hint={
              !isNew && server!.has_auth_token
                ? "Token is set. Leave empty to keep it; type a new value to replace; check 'Clear' to remove."
                : "Optional. Sent as 'Authorization: Bearer <token>'."
            }
          >
            <input
              type="password"
              value={authToken}
              onChange={(e) => setAuthToken(e.target.value)}
              placeholder={!isNew && server!.has_auth_token ? "•••••• (set)" : "sk-... or your provider's token"}
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100 font-mono"
            />
            {!isNew && server!.has_auth_token && (
              <label className="flex items-center gap-1.5 mt-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={clearToken}
                  onChange={(e) => setClearToken(e.target.checked)}
                  className="w-3.5 h-3.5"
                />
                <span className="text-slate-600 dark:text-slate-400">Clear existing token</span>
              </label>
            )}
          </Field>
          <Field label="Description (optional)">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100"
              placeholder="What this server provides"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="w-4 h-4"
            />
            <span className="text-slate-700 dark:text-slate-300">Enabled</span>
            <span className="text-xs text-slate-500 dark:text-slate-500">— ledger-core's AI will use this server's tools</span>
          </label>
          {saveMut.error && (
            <div className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-sm text-red-700 dark:text-red-300">
              {errorMessage(saveMut.error)}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 dark:border-gray-800 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 dark:border-gray-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending || !name.trim() || !url.trim()}
            className="px-4 py-1.5 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMut.isPending ? "Saving…" : isNew ? "Add server" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}
